/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `openpayTokenization` for the Caintra Connect frontend.
 * @module utils/payment/openpayTokenization
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../logger.js";

const DEFAULT_TOKENIZATION_TIMEOUT_MS = 15000;

/**
 * Tokenizes card data through the OpenPay SDK with a timeout guard.
 * @param {Object} OpenPay - Configured OpenPay client
 * @param {Object} cardData - OpenPay card payload
 * @param {number} [timeoutMs=15000] - Max wait time for tokenization
 * @returns {Promise<Object>} OpenPay token response data
 */
export const tokenizeCard = (
  OpenPay,
  cardData,
  timeoutMs = DEFAULT_TOKENIZATION_TIMEOUT_MS,
) => {
  return Promise.race([
    new Promise((resolve, reject) => {
      OpenPay.token.create(cardData, (response) => {
        logger.info("OpenPay Response: ", { data: response });
        if (response.status === 200) {
          resolve(response.data);
        } else {
          logger.error("OpenPay Error Details: ", { data: response });
          reject(new Error("Card tokenization failed"));
        }
      });
    }),
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error("Tokenization timeout"));
      }, timeoutMs);
    }),
  ]);
};
